import {ActionIcon, Box, Divider, Flex} from '@mantine/core';
import {IconFilter} from '@tabler/icons-react';
import React, {useState} from 'react';
import {Description} from 'src/design/atoms/Description';
import {Textbox} from 'src/design/atoms/Textbox';
import {ContentHeader, Subtitle} from 'src/design/atoms/Typography';
import {Title} from 'src/design/atoms/Title';

/**
 * List page header: title/description on the left, actions + filter toggle
 * + search box on the right, with a collapsible filter row underneath.
 * @registry List header with title, actions, search, and a toggleable filter row.
 *
 * Ported from `library/tables/DataHeader`. Prop surface kept identical.
 * DEVIATION: the frozen component was MUI (`Box`/`Divider` +
 * `DataHeader.styles.js`, both banned in `design/`). Rebuilt on Mantine
 * `Box`/`Divider`/`Flex`/`ActionIcon`, replacing MUI responsive `sx` object
 * breakpoints with Mantine's `{base, ...}` responsive style props and the
 * MUI `Pagination`-style filter icon color swap (`primary.main` /
 * `secondary.main`) with a Mantine `ActionIcon` `variant`/`color` toggle
 * (`subtle`/`gray` when inactive, `light`/`blue` when the filter row is
 * open). Visually equivalent (icon button that highlights when the filter
 * row is expanded); exact hex values are not reproduced.
 */
export interface DataHeaderProps {
  searchTerm?: string;
  isFilterActive: boolean;
  titleText: string;
  titleSize?: 'sm' | 'md' | 'lg';
  description?: string;
  actions?: React.ReactNode[];
  filters?: React.ReactNode[];
  showFiltersByDefault?: boolean;

  onSearchTermChange?: React.ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  >;
}

export const DataHeader: React.FC<DataHeaderProps> = ({
  searchTerm,
  actions,
  filters,
  titleText,
  titleSize = 'lg',
  description,
  showFiltersByDefault,
  onSearchTermChange,
}) => {
  const [showFilterRow, setShowFilterRow] = useState(
    showFiltersByDefault ?? false
  );

  return (
    <Box mb="0.25rem" style={{display: 'flex', flexDirection: 'column', minHeight: '55px'}}>
      <Flex
        justify={{base: 'normal', xl: 'space-between'}}
        direction={{base: 'column', xl: 'row'}}>
        <Box>
          {titleSize === 'lg' && <Title>{titleText}</Title>}
          {titleSize === 'md' && <ContentHeader text={titleText} />}
          {titleSize === 'sm' && <Subtitle text={titleText} />}
          {description && <Description pb="md" text={description} />}
        </Box>
        <Flex
          direction={{base: 'column', lg: 'row'}}
          justify={{base: 'flex-start', lg: 'normal', xl: 'flex-end'}}
          pt={{base: 'md', xl: 0}}>
          <Flex direction={{base: 'column', lg: 'row'}} justify="center">
            {actions &&
              actions.map((action, index) => (
                <Box
                  key={index}
                  pt={{base: 'xs', lg: 0}}
                  pr={{base: 0, md: 'xs', lg: 'lg'}}>
                  {action}
                </Box>
              ))}
            {filters && filters.length > 0 && (
              <Box
                my="auto"
                pt={{base: 'xs', lg: 0}}
                pr={{base: 0, lg: 'lg'}}>
                <ActionIcon
                  variant={showFilterRow ? 'light' : 'subtle'}
                  color={showFilterRow ? 'blue' : 'gray'}
                  size="lg"
                  aria-label="Toggle filters"
                  onClick={() => setShowFilterRow(!showFilterRow)}>
                  <IconFilter size={20} />
                </ActionIcon>
              </Box>
            )}
            {onSearchTermChange && (
              <Box pt={{base: 'xs', lg: 0}} pr={{base: 0, lg: 'lg'}}>
                <Textbox
                  placeholder="Search"
                  ariaLabel="searchIndex"
                  name="search"
                  value={searchTerm}
                  onChangeCallback={onSearchTermChange}
                  size="lg"
                />
              </Box>
            )}
          </Flex>
        </Flex>
      </Flex>
      {showFilterRow && (
        <>
          <Box mt="sm" mb="sm">
            <Divider />
          </Box>
          <Flex direction={{base: 'column', md: 'row'}}>
            {filters &&
              filters.map((filter, index) => (
                <Box key={index} mr="md" my="auto">
                  {filter}
                </Box>
              ))}
          </Flex>
        </>
      )}
    </Box>
  );
};
